/**
 * One-time coach mark shown on the first wrong typed answer.
 * Explains the retype mechanic (the user must type the correct word to advance).
 *
 * Render contract:
 *   - Caller (LearnPage) already portals the `overlays` block to document.body,
 *     so this component uses position:fixed directly — no inner portal needed.
 *   - Never steals input focus: all interactive elements use preventDefault on
 *     pointerdown/mousedown (ADR-011/013 — keyboard must stay raised).
 *   - Zero layout shift: entirely outside the page grid (ADR-012).
 *
 * Persistence: caller writes 'cs_retype_coach_shown' to localStorage on dismiss.
 * Shown at most once per device across all sessions.
 */
import { AnimatePresence, motion } from 'framer-motion'

interface Props {
  visible: boolean
  onDismiss: () => void
  isMobile: boolean
  title: string
  body: string
  dismissLabel: string
}

export default function RetypeCoachMark({ visible, onDismiss, isMobile, title, body, dismissLabel }: Props) {
  // Position the bubble above the pinned input zone:
  //   Mobile: answer bar (64px) + container padding (~10px) ≈ 74px. The
  //     soft-reject slot that used to add 38px here was removed (ADR-048
  //     §7 amendment, 2026-07-15 — guidance moved into the placeholder).
  //     Use 95px for a comfortable margin.
  //   Desktop: input (~52px) + submit zone (50px) + hint (~24px) + padding (~14px) ≈ 140px.
  //     Use 165px for a comfortable margin.
  const bottomOffset = isMobile ? 95 : 165

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="retype-coach"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: bottomOffset,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 100,
          }}
          // Not an ARIA landmark — guidance UI only; screen readers can still
          // interact with the dismiss button via normal focus order.
          aria-live="polite"
        >
          <div
            style={{
              pointerEvents: 'auto',
              width: 'min(300px, calc(100vw - 32px))',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              boxShadow: 'var(--shadow-card)',
              padding: '14px 16px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              position: 'relative',
            }}
          >
            {/* Title */}
            <p style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.35,
            }}>
              {title}
            </p>

            {/* Body copy */}
            <p style={{
              margin: 0,
              fontSize: 12,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
            }}>
              {body}
            </p>

            {/* Dismiss button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
              <button
                type="button"
                // Keep keyboard raised (ADR-011/013)
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onDismiss}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--accent)',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  padding: '4px 0',
                  lineHeight: 1,
                }}
              >
                {dismissLabel}
              </button>
            </div>

            {/* Downward caret pointing at the input area below */}
            <div aria-hidden style={{
              position: 'absolute',
              bottom: -8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid var(--border)',
            }} />
            <div aria-hidden style={{
              position: 'absolute',
              bottom: -6,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: '7px solid var(--bg-surface)',
            }} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
